/**
 * contracts-page.tsx — Platform → Hợp đồng (ký tay online, khách cá nhân). Route `/platform/contracts`.
 *
 * Tab "Hợp đồng": danh sách + lọc + thao tác nhanh. Tab "Mẫu hợp đồng": bên bán, ẢNH CHỮ KÝ bên bán (ký sẵn), hạn ký, điều khoản.
 * Khách ký bằng chữ ký vẽ tay + mã OTP email; dấu vết (IP, giờ, mã băm) lưu làm chứng cứ — server: modules/platform/contract.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ChevronRight, Download, Eye, Loader2, Pencil, Plus, Search, Trash2, Upload } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { platformApiError } from '@/lib/platform-client'
import { openContractPdf, useContractTemplate, useContracts, useSaveContractTemplate } from '@/hooks/use-contracts'
import { ClauseEditor } from './clause-editor'
import {
  fmtDateTime, fmtMoney, STATUS_LABELS, STATUS_VARIANT, timeLeft, type Clause, type ContractListItem, type ContractStatus, type ContractTemplate,
} from './contract-helpers'

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Tất cả' },
  ...(['draft', 'sent', 'signed', 'declined', 'expired', 'cancelled'] as ContractStatus[]).map((s) => ({ value: s, label: STATUS_LABELS[s] })),
]

export function ContractsPage() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'template' ? 'template' : 'list'
  return (
    <div className="space-y-4">
      <PageHeader
        title="Hợp đồng"
        description="Lập hợp đồng dịch vụ cho khách cá nhân, gửi link ký online (chữ ký vẽ tay + mã OTP email). Bên bán ký sẵn bằng ảnh chữ ký trong Mẫu hợp đồng; dấu vết ký (IP, thời điểm, mã băm) được lưu làm chứng cứ."
        actions={<Button asChild><Link to="/platform/contracts/new"><Plus className="h-4 w-4" /> Lập hợp đồng</Link></Button>}
      />
      <Tabs value={tab} onValueChange={(v) => setParams(v === 'template' ? { tab: 'template' } : {}, { replace: true })}>
        <TabsList>
          <TabsTrigger value="list">Hợp đồng</TabsTrigger>
          <TabsTrigger value="template">Mẫu hợp đồng</TabsTrigger>
        </TabsList>
        <TabsContent value="list" className="mt-4"><ContractList /></TabsContent>
        <TabsContent value="template" className="mt-4"><TemplateEditor /></TabsContent>
      </Tabs>
    </div>
  )
}

function ContractList() {
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useContracts({ status: status || undefined, q: search || undefined, page })

  useEffect(() => {
    const t = setTimeout(() => { setSearch(q.trim()); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [q])

  const navigate = useNavigate()
  const pdf = (r: ContractListItem, kind: 'pdf' | 'preview-pdf') =>
    openContractPdf(r.id, kind, `${r.number}-da-ky.pdf`).catch((err) => toast.error(platformApiError(err)))

  const columns: Column<ContractListItem>[] = [
    {
      key: 'number', header: 'Số',
      cell: (r) => <Link to={`/platform/contracts/${r.id}`} className="whitespace-nowrap font-mono font-medium hover:text-primary">{r.number}</Link>,
    },
    {
      key: 'customer', header: 'Khách hàng',
      cell: (r) => (
        <div className="min-w-0 max-w-[260px]">
          <p className="truncate font-medium" title={r.customerName}>{r.customerName || '—'}</p>
          <p className="truncate text-xs text-muted-foreground" title={r.customerEmail}>{r.customerEmail}</p>
        </div>
      ),
    },
    {
      key: 'deal', header: 'Nội dung',
      cell: (r) => (
        <div className="max-w-[240px]">
          <p className="truncate" title={r.description}>{r.description || '—'}</p>
          <p className="text-xs text-muted-foreground">{fmtMoney(r.amount)}</p>
        </div>
      ),
    },
    {
      key: 'status', header: 'Trạng thái',
      cell: (r) => (
        <div className="whitespace-nowrap">
          <Badge variant={STATUS_VARIANT[r.displayStatus]}>{STATUS_LABELS[r.displayStatus]}</Badge>
          {r.displayStatus === 'sent' && r.expiresAt && <p className="mt-0.5 text-xs text-muted-foreground">{timeLeft(r.expiresAt)}</p>}
          {r.displayStatus === 'signed' && <p className="mt-0.5 text-xs text-muted-foreground">{fmtDateTime(r.signedAt)}</p>}
        </div>
      ),
    },
    { key: 'created', header: 'Ngày lập', cell: (r) => <span className="whitespace-nowrap text-xs text-muted-foreground">{fmtDateTime(r.createdAt)}</span> },
    {
      key: 'actions', header: '', align: 'right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {r.status === 'draft' ? (
            <Button asChild size="sm"><Link to={`/platform/contracts/${r.id}`}><Pencil className="h-3.5 w-3.5" /> Sửa & gửi</Link></Button>
          ) : (
            <Button asChild size="sm" variant="outline"><Link to={`/platform/contracts/${r.id}`}>Chi tiết <ChevronRight className="h-3.5 w-3.5" /></Link></Button>
          )}
          <Button size="icon" variant="ghost" title="Xem trước PDF" onClick={() => pdf(r, 'preview-pdf')}><Eye className="h-4 w-4" /></Button>
          {r.status === 'signed' && (
            <Button size="icon" variant="ghost" title="Tải PDF đã ký" onClick={() => pdf(r, 'pdf')}><Download className="h-4 w-4 text-success" /></Button>
          )}
        </div>
      ),
    },
  ]

  if (isError) return <ErrorState message="Không tải được danh sách hợp đồng" />
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Số hợp đồng, tên khách, email…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-1">
          {STATUS_FILTERS.map((f) => (
            <Button key={f.value} size="sm" variant={status === f.value ? 'default' : 'outline'} onClick={() => { setStatus(f.value); setPage(1) }}>{f.label}</Button>
          ))}
        </div>
      </div>
      <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(r) => r.id} emptyTitle="Chưa có hợp đồng" onRowClick={(r) => navigate(`/platform/contracts/${r.id}`)} />
      {data && data.total > data.pageSize && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Trước</Button>
          <span>Trang {page}/{totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Sau</Button>
        </div>
      )}
    </div>
  )
}

const MAX_SIG_BYTES = 200 * 1024

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '')
    r.onerror = () => reject(new Error('Không đọc được tệp'))
    r.readAsDataURL(file)
  })
}

const TEXT_FIELDS: { key: 'companyName' | 'taxCode' | 'address' | 'phone' | 'email' | 'signerName' | 'signerTitle'; label: string; wide?: boolean }[] = [
  { key: 'companyName', label: 'Tên công ty bên bán (Bên A)', wide: true },
  { key: 'address', label: 'Địa chỉ', wide: true },
  { key: 'taxCode', label: 'Mã số thuế' },
  { key: 'phone', label: 'Điện thoại' },
  { key: 'email', label: 'Email' },
  { key: 'signerName', label: 'Người đại diện ký' },
  { key: 'signerTitle', label: 'Chức danh' },
]

function TemplateEditor() {
  const { data, isLoading, isError } = useContractTemplate()
  const save = useSaveContractTemplate()
  const [form, setForm] = useState<ContractTemplate | null>(null)
  const [newSig, setNewSig] = useState<string | null>(null) // base64 PNG mới chọn (chưa lưu)
  const [removeSig, setRemoveSig] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  useEffect(() => { if (data && !form) setForm(data.template) }, [data, form])
  if (isLoading || !form || !data) return isError ? <ErrorState message="Không tải được mẫu hợp đồng" /> : <Loading label="Đang tải..." />

  const preview = newSig ? `data:image/png;base64,${newSig}` : removeSig ? null : data.signaturePreview

  const pick = async (file: File | undefined) => {
    if (!file) return
    if (file.type !== 'image/png') return void toast.error('Ảnh chữ ký phải là PNG (nên nền trong suốt)')
    if (file.size > MAX_SIG_BYTES) return void toast.error('Ảnh chữ ký tối đa 200KB')
    try { setNewSig(await readAsBase64(file)); setRemoveSig(false) } catch (e) { toast.error((e as Error).message) }
  }

  const submit = () => {
    const { hasSignature: _h, ...rest } = form
    void _h
    save.mutate(
      { ...rest, ...(newSig ? { signatureB64: newSig } : {}), ...(removeSig && !newSig ? { removeSignature: true } : {}) },
      {
        onSuccess: (r) => { toast.success('Đã lưu mẫu hợp đồng'); setForm(r.template); setNewSig(null); setRemoveSig(false) },
        onError: (err) => toast.error(platformApiError(err)),
      },
    )
  }

  return (
    <div className="max-w-3xl space-y-4 rounded-lg border bg-card p-4">
      <p className="text-sm text-muted-foreground">
        Mẫu dùng khi lập hợp đồng mới (mỗi hợp đồng chụp lại mẫu lúc lập — sửa mẫu không đổi hợp đồng đã lập).
        {data.updatedAt ? ` Lưu lần cuối ${fmtDateTime(data.updatedAt)}.` : ' Đang dùng mặc định.'}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {TEXT_FIELDS.map((f) => (
          <div key={f.key} className={f.wide ? 'space-y-1 sm:col-span-2' : 'space-y-1'}>
            <Label htmlFor={`ct-${f.key}`}>{f.label}</Label>
            <Input id={`ct-${f.key}`} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
          </div>
        ))}
        <div className="space-y-1">
          <Label htmlFor="ct-days">Hạn ký mặc định (ngày kể từ lúc gửi)</Label>
          <Input id="ct-days" type="number" min={1} max={90} value={form.expiresDays} onChange={(e) => setForm({ ...form, expiresDays: Number(e.target.value) || 1 })} />
        </div>
      </div>

      <div className="space-y-2 rounded-md border p-3">
        <Label>Chữ ký bên bán (ký sẵn trên mọi hợp đồng)</Label>
        <p className="text-xs text-muted-foreground">Tải ảnh PNG (nên nền trong suốt, ≤200KB). Chưa có ảnh này thì không gửi được hợp đồng.</p>
        <div className="flex flex-wrap items-center gap-3">
          {preview
            ? <img src={preview} alt="Chữ ký bên bán" className="h-16 max-w-[200px] rounded border bg-white object-contain p-1" />
            : <span className="rounded border border-dashed px-3 py-4 text-xs text-muted-foreground">Chưa có ảnh chữ ký</span>}
          <input ref={fileRef} type="file" accept="image/png" className="hidden" onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = '' }} />
          <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}><Upload className="h-4 w-4" /> Tải ảnh chữ ký</Button>
          {preview && <Button type="button" variant="ghost" size="sm" onClick={() => { setNewSig(null); setRemoveSig(true) }}><Trash2 className="h-4 w-4 text-destructive" /> Gỡ</Button>}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Điều khoản mặc định</Label>
        <ClauseEditor clauses={form.clauses} variables={data.variables} onChange={(clauses: Clause[]) => setForm({ ...form, clauses })} />
      </div>

      <div className="flex justify-end">
        <Button disabled={save.isPending} onClick={submit}>
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Lưu mẫu
        </Button>
      </div>
    </div>
  )
}
